import { Box, Button, ButtonGroup, Collapse, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'

type GroupsTabDynamicIslandProps = {
  assignedCapacity: number
  totalCapacity: number
  selectedCapacity: number
  selectedCount: number
  filteredCount: number
  bidLevels: BidLevelInput[]
  onAssignToLevel: (levelId: string) => void
  onClearSelection: () => void
  onSelectAll: () => void
}

function formatCapacity(mw: number): string {
  return mw >= 10 ? mw.toFixed(1) : mw.toFixed(2)
}

function GroupsTabDynamicIsland({
  assignedCapacity,
  totalCapacity,
  selectedCapacity,
  selectedCount,
  filteredCount,
  bidLevels,
  onAssignToLevel,
  onClearSelection,
  onSelectAll,
}: Readonly<GroupsTabDynamicIslandProps>) {
  const { t } = useTranslation()

  const hasSelection = selectedCount > 0

  return (
    <Stack
      gap={hasSelection ? 0.75 : 0}
      sx={{
        mb: 2,
        p: 1,
        backgroundColor: hasSelection ? 'primary.50' : 'grey.100',
        borderRadius: 1,
        transition: 'background-color 0.15s ease-in-out',
        border: 1,
        borderColor: hasSelection ? 'primary.200' : 'transparent',
      }}
    >
      {/* Top row - capacity info + action button (always same position) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {hasSelection ? (
          <CustomTypography variant="caption">
            <Box component="span" sx={{ fontWeight: 600 }}>
              {selectedCount}
            </Box>{' '}
            {t('bidding.bidding_cascading.groups_tab.selected')} • {formatCapacity(selectedCapacity)} MW
          </CustomTypography>
        ) : (
          <CustomTypography color="text.secondary" variant="caption">
            <span data-testid="assigned-capacity">{formatCapacity(assignedCapacity)}</span>
            {' / '}
            <span data-testid="total-capacity">{formatCapacity(totalCapacity)}</span>
            {' MW '}
            {t('bidding.bidding_cascading.groups_tab.capacity_summary.assigned').toLowerCase()}
          </CustomTypography>
        )}
        <Button
          disabled={!hasSelection && filteredCount === 0}
          size="small"
          sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem' }}
          variant="text"
          onClick={hasSelection ? onClearSelection : onSelectAll}
        >
          {hasSelection ? t('common.clear') : t('bidding.bidding_cascading.groups_tab.actions.select_all')}
        </Button>
      </Box>

      {/* Bottom row - assign to level (only when selected) */}
      <Collapse in={hasSelection} timeout={150}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5 }}>
          <CustomTypography color="text.secondary" variant="caption">
            {t('bidding.bidding_cascading.groups_tab.actions.assign_to_level')}
          </CustomTypography>
          <ButtonGroup size="small" variant="outlined">
            {bidLevels.map((level, index) => (
              <Button
                key={level.id}
                sx={{
                  minWidth: 28,
                  px: 0.75,
                  py: 0.25,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
                onClick={() => onAssignToLevel(level.id)}
              >
                {index + 1}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      </Collapse>
    </Stack>
  )
}

export default GroupsTabDynamicIsland
